import React, { useRef, useCallback } from "react";

/**
 * DecorationSvg — SVG-Presets für Saal-Deko mit Drag & Drop / Resize / Rotate.
 * Pure-CSS-Positionierung (Prozent relativ zum Container).
 */

const CURTAIN_LEFT = (color = "#7A1921") => (
  <svg viewBox="0 0 100 200" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block" }} aria-hidden>
    <defs>
      <linearGradient id="c-left-g" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor={color} stopOpacity="0.9" />
        <stop offset="0.5" stopColor={color} stopOpacity="0.55" />
        <stop offset="1" stopColor={color} stopOpacity="0.9" />
      </linearGradient>
    </defs>
    {Array.from({ length: 6 }).map((_, i) => (
      <path key={i}
        d={`M${i * 16} 0 Q${i * 16 + 10} 25 ${i * 16 + 4} 50 T${i * 16 + 6} 100 T${i * 16 + 4} 150 T${i * 16 + 6} 200 L${i * 16 + 16} 200 L${i * 16 + 16} 0 Z`}
        fill="url(#c-left-g)" stroke={color} strokeOpacity="0.35" strokeWidth="0.4" />
    ))}
  </svg>
);

const CURTAIN_RIGHT = (color = "#7A1921") => (
  <div style={{ width: "100%", height: "100%", transform: "scaleX(-1)" }}>{CURTAIN_LEFT(color)}</div>
);

const EXIT_DOOR = (color = "#8FBC8F") => (
  <svg viewBox="0 0 100 80" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block" }} aria-hidden>
    <rect x="4" y="4" width="92" height="72" rx="4" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="2" />
    <path d="M50 20 L34 40 L44 40 L44 60 L56 60 L56 40 L66 40 Z" fill={color} />
    <text x="50" y="76" fontSize="10" textAnchor="middle" fill={color} fontFamily="Helvetica-Bold, sans-serif" fontWeight="bold" letterSpacing="1.5">EXIT</text>
  </svg>
);

const SPOTLIGHT = (color = "#D4AF37") => (
  <svg viewBox="0 0 60 60" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block" }} aria-hidden>
    <defs>
      <radialGradient id="s-g" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor={color} stopOpacity="0.9" />
        <stop offset="0.4" stopColor={color} stopOpacity="0.4" />
        <stop offset="1" stopColor={color} stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="30" cy="30" r="28" fill="url(#s-g)" />
    <circle cx="30" cy="30" r="6" fill={color} />
  </svg>
);

const PROJECTOR = (color = "#575148") => (
  <svg viewBox="0 0 100 60" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block" }} aria-hidden>
    <rect x="10" y="18" width="70" height="30" rx="4" fill={color} stroke={color} />
    <circle cx="75" cy="33" r="10" fill="#0A0A0A" stroke={color} strokeWidth="2" />
    <circle cx="75" cy="33" r="4" fill={color} />
    <rect x="15" y="12" width="30" height="8" rx="2" fill={color} fillOpacity="0.5" />
    <path d="M85 33 L100 25 L100 41 Z" fill={color} fillOpacity="0.25" />
  </svg>
);

const STAGE = (color = "#8B7328") => (
  <svg viewBox="0 0 200 40" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block" }} aria-hidden>
    <path d="M0 40 L20 8 L180 8 L200 40 Z" fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1.5" />
    <text x="100" y="28" fontSize="11" textAnchor="middle" fill={color} fontFamily="Helvetica-Bold, sans-serif" fontWeight="bold" letterSpacing="4">BÜHNE</text>
  </svg>
);

const POPCORN_BAR = (color = "#EEDC82") => (
  <svg viewBox="0 0 120 60" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block" }} aria-hidden>
    <rect x="6" y="20" width="108" height="30" rx="4" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <path d="M20 40 L20 20 Q20 12 30 12 Q40 12 40 20 L40 40 Z" fill={color} fillOpacity="0.5" stroke={color} />
    <text x="70" y="38" fontSize="10" textAnchor="middle" fill={color} fontFamily="Helvetica-Bold, sans-serif" fontWeight="bold" letterSpacing="2">SNACKBAR</text>
  </svg>
);

const ARROW = (color = "#D4AF37") => (
  <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block" }} aria-hidden>
    <path d="M4 15 L44 15 M36 6 L48 15 L36 24" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LABEL_TEXT = (color = "#D4AF37", label = "Text") => (
  <svg viewBox="0 0 200 32" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block" }} aria-hidden>
    <text x="100" y="22" fontSize="14" textAnchor="middle" fill={color} fontFamily="Helvetica-Bold, sans-serif" fontWeight="bold" letterSpacing="3">
      {(label || "").toUpperCase()}
    </text>
  </svg>
);

const SPEAKER = (color = "#575148") => (
  <svg viewBox="0 0 40 60" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block" }} aria-hidden>
    <rect x="4" y="4" width="32" height="52" rx="3" fill={color} fillOpacity="0.4" stroke={color} strokeWidth="1.5" />
    <circle cx="20" cy="18" r="6" fill="#0A0A0A" stroke={color} />
    <circle cx="20" cy="40" r="10" fill="#0A0A0A" stroke={color} strokeWidth="1.5" />
    <circle cx="20" cy="40" r="4" fill={color} fillOpacity="0.6" />
  </svg>
);

export const DECO_PRESETS = [
  { type: "curtain-left",  label: "Vorhang links",  render: CURTAIN_LEFT,  defaultColor: "#7A1921", widthPct: 8,  aspect: 100/200, dw: 6,  dh: 40 },
  { type: "curtain-right", label: "Vorhang rechts", render: CURTAIN_RIGHT, defaultColor: "#7A1921", widthPct: 8,  aspect: 100/200, dw: 94, dh: 40 },
  { type: "exit",          label: "Notausgang",     render: EXIT_DOOR,     defaultColor: "#8FBC8F", widthPct: 10, aspect: 100/80,  dw: 10, dh: 90 },
  { type: "spot",          label: "Spotlight",      render: SPOTLIGHT,     defaultColor: "#D4AF37", widthPct: 8,  aspect: 1,       dw: 50, dh: 6 },
  { type: "projector",     label: "Projektor",      render: PROJECTOR,     defaultColor: "#575148", widthPct: 12, aspect: 100/60,  dw: 50, dh: 95 },
  { type: "stage",         label: "Bühne",          render: STAGE,         defaultColor: "#8B7328", widthPct: 40, aspect: 200/40,  dw: 50, dh: 15 },
  { type: "popcorn",       label: "Snackbar",       render: POPCORN_BAR,   defaultColor: "#EEDC82", widthPct: 22, aspect: 120/60,  dw: 50, dh: 92 },
  { type: "arrow",         label: "Pfeil",          render: ARROW,         defaultColor: "#D4AF37", widthPct: 10, aspect: 60/30,   dw: 50, dh: 55 },
  { type: "speaker",       label: "Lautsprecher",   render: SPEAKER,       defaultColor: "#575148", widthPct: 5,  aspect: 40/60,   dw: 15, dh: 15 },
  { type: "label",         label: "Text-Etikett",   render: LABEL_TEXT,    defaultColor: "#D4AF37", widthPct: 20, aspect: 200/32,  dw: 50, dh: 4,  hasText: true },
  { type: "image",         label: "Eigenes Bild",   render: null,          defaultColor: "#D4AF37", widthPct: 20, aspect: 1,       dw: 50, dh: 50, isImage: true },
];

function _clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

export function DecorationItem({ deco, selected = false, onSelect, onUpdate, containerRef, draggable = false }) {
  const preset = DECO_PRESETS.find((p) => p.type === deco.type);
  const rootRef = useRef(null);
  const dragState = useRef(null);

  const beginDrag = useCallback((mode) => (e) => {
    if (!draggable || !onUpdate || !containerRef?.current) return;
    e.stopPropagation();
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    dragState.current = {
      mode,
      startX: e.clientX, startY: e.clientY,
      rect,
      startVal: { x: Number(deco.x) || 50, y: Number(deco.y) || 50, scale: Number(deco.scale) || 1, rotation: Number(deco.rotation) || 0 },
    };
    if (onSelect) onSelect(deco.id);
    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
  }, [deco, draggable, onUpdate, onSelect, containerRef]);

  const onMove = useCallback((e) => {
    const st = dragState.current;
    if (!st) return;
    const { rect, startVal, mode } = st;
    const dxPx = e.clientX - st.startX;
    const dyPx = e.clientY - st.startY;
    if (mode === "move") {
      const nx = _clamp(startVal.x + (dxPx / rect.width) * 100, 0, 100);
      const ny = _clamp(startVal.y + (dyPx / rect.height) * 100, 0, 100);
      onUpdate(deco.id, { x: nx, y: ny });
    } else if (mode === "scale") {
      const factor = 1 + (dxPx / rect.width) * 3 + (dyPx / rect.height) * 3;
      const ns = _clamp(startVal.scale * factor, 0.2, 6);
      onUpdate(deco.id, { scale: ns });
    } else if (mode === "rotate") {
      const el = rootRef.current;
      if (!el) return;
      const eRect = el.getBoundingClientRect();
      const cx = eRect.left + eRect.width / 2;
      const cy = eRect.top + eRect.height / 2;
      const ang = (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI + 90;
      onUpdate(deco.id, { rotation: Math.round(ang) });
    }
  }, [deco.id, onUpdate]);

  const endDrag = useCallback((e) => {
    dragState.current = null;
    try { e.currentTarget?.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
  }, []);

  if (!preset) return null;
  const scale = deco.scale ?? 1;
  const w = preset.widthPct * scale;
  const color = deco.color || preset.defaultColor;
  const rot = deco.rotation ?? 0;
  const opacity = deco.opacity ?? 0.9;
  const rendered = preset.isImage
    ? (deco.image_url
        ? <img src={deco.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }} />
        : <div className="w-full h-full flex items-center justify-center text-[10px] text-[#575148] border border-dashed border-[#575148]/60 rounded">Bild fehlt</div>)
    : (preset.hasText ? preset.render(color, deco.label || "") : preset.render(color));
  const style = {
    position: "absolute",
    left: `${deco.x ?? preset.dw}%`,
    top: `${deco.y ?? preset.dh}%`,
    width: `${w}%`,
    aspectRatio: preset.aspect,
    transform: `translate(-50%, -50%) rotate(${rot}deg)`,
    opacity,
    pointerEvents: (draggable || onSelect) ? "auto" : "none",
    cursor: draggable ? (dragState.current ? "grabbing" : "grab") : (onSelect ? "pointer" : "default"),
    outline: selected ? "2px dashed #D4AF37" : "none",
    outlineOffset: 4,
    touchAction: "none",
    userSelect: "none",
  };
  return (
    <div
      ref={rootRef}
      style={style}
      onPointerDown={draggable ? beginDrag("move") : (onSelect ? (e) => { e.stopPropagation(); onSelect(deco.id); } : undefined)}
      onPointerMove={draggable ? onMove : undefined}
      onPointerUp={draggable ? endDrag : undefined}
      onPointerCancel={draggable ? endDrag : undefined}
      data-deco-type={deco.type}
      data-testid={`deco-item-${deco.type}`}
    >
      {rendered}
      {selected && draggable && (
        <>
          {/* Rotations-Handle oben */}
          <div
            onPointerDown={beginDrag("rotate")}
            onPointerMove={onMove}
            onPointerUp={endDrag}
            title="Rotieren"
            data-testid="deco-handle-rotate"
            style={{
              position: "absolute", left: "50%", top: -22, width: 14, height: 14,
              transform: "translate(-50%, 0)", borderRadius: "50%",
              background: "#0A0A0A", border: "2px solid #D4AF37", cursor: "grab",
              boxShadow: "0 0 0 3px rgba(212,175,55,0.15)",
            }}
          />
          <div style={{ position: "absolute", left: "50%", top: -10, width: 1, height: 12, background: "#D4AF37", opacity: 0.6, pointerEvents: "none" }} />
          {/* Skalier-Handle unten-rechts */}
          <div
            onPointerDown={beginDrag("scale")}
            onPointerMove={onMove}
            onPointerUp={endDrag}
            title="Skalieren"
            data-testid="deco-handle-scale"
            style={{
              position: "absolute", right: -8, bottom: -8, width: 14, height: 14,
              borderRadius: 2, background: "#D4AF37", border: "2px solid #0A0A0A",
              cursor: "nwse-resize",
              boxShadow: "0 0 0 3px rgba(212,175,55,0.15)",
            }}
          />
        </>
      )}
    </div>
  );
}

export function DecorationLayer({ decorations = [], selectedId = null, onSelect = null, onUpdate = null, draggable = false, containerRef = null }) {
  if (!decorations?.length) return null;
  return (
    <div className="absolute inset-0" style={{ pointerEvents: "none" }} aria-hidden>
      {decorations.map((d, i) => (
        <DecorationItem
          key={d.id || i}
          deco={d}
          selected={selectedId === (d.id || i)}
          onSelect={onSelect ? (id) => onSelect(id) : null}
          onUpdate={onUpdate}
          containerRef={containerRef}
          draggable={draggable}
        />
      ))}
    </div>
  );
}

export default DECO_PRESETS;
